import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { MinusCircle } from "components/icons/MinusCircle";
import { PlusCircle } from "components/icons/PlusCircle";
import { TrashSimple } from "components/icons/TrashSimple";
import {
  AddBulkCartItem,
  BULK_CART_ACTIONS,
  BULK_CART_ACTIONS_DISPLAY,
} from "constants/BulkCartAction";
import { createColumnHelper } from "react-table-8.10.7";
import { SelectBaseTask } from "./SelectBaseTask";
import {
  FORM_5471_FOREIGN_SUBSIDIARY_REPORTING,
  FORM_5472_REPORTABLE_TRANSACTIONS,
} from "types/Models/services";
import { createInitialRow } from "./AddDetailsForBulkCartAction";

const ACTION_OPTIONS: OptionData[] = [
  {
    value: BULK_CART_ACTIONS.ADD_TO_CART,
    label: BULK_CART_ACTIONS_DISPLAY[BULK_CART_ACTIONS.ADD_TO_CART],
  },
  {
    value: BULK_CART_ACTIONS.SAVED_FOR_LATER,
    label: BULK_CART_ACTIONS_DISPLAY[BULK_CART_ACTIONS.SAVED_FOR_LATER],
  },
  {
    value: BULK_CART_ACTIONS.DELETE,
    label: BULK_CART_ACTIONS_DISPLAY[BULK_CART_ACTIONS.DELETE],
  },
];

const createColumn = createColumnHelper<AddBulkCartItem>();

const hasRowValue = (row: AddBulkCartItem) => {
  return Boolean(row.product_id || row.action || row.season);
};

export const createAddDetailsTableColumns = (
  setFieldValue: (field: string, value: any) => void,
  rows: AddBulkCartItem[],
  removeRow: (index: number) => void,
  replaceRow: (index: number, value: AddBulkCartItem) => void
) => [
  createColumn.accessor("product_id", {
    header: "TASK",
    size: 30,
    cell: (info) => {
      const value = info.getValue();
      return <SelectBaseTask index={info.row.index} value={value} />;
    },
  }),
  createColumn.accessor("season", {
    id: "season",
    header: "PERIOD",
    size: 20,
    cell: (info) => {
      return (
        <div className="t-text-body-sm t-text-text-30">
          {info.getValue() ? `Season ${info.getValue()}` : "- -"}
        </div>
      );
    },
  }),
  createColumn.accessor("action", {
    header: "ACTION",
    size: 30,
    cell: (info) => {
      const index = info.row.index;
      const value = info.getValue();

      return (
        <Combobox
          name={`rows.${index}.action`}
          placeholder="Select action"
          options={ACTION_OPTIONS}
          value={ACTION_OPTIONS.find((opt) => opt.value === value)}
          size="small"
          fullWidth
          withForm
          menuPortalTarget={document.body}
        />
      );
    },
  }),
  createColumn.accessor("quantity", {
    header: "QUANTITY",
    size: 20,
    cell: (info) => {
      const index = info.row.index;
      const quantity = info.getValue();
      const rowData = info.row.original;
      const shouldShowQuantityUpdator =
        rowData.base_task_key === FORM_5471_FOREIGN_SUBSIDIARY_REPORTING ||
        rowData.base_task_key === FORM_5472_REPORTABLE_TRANSACTIONS;

      return (
        <div className="t-flex t-items-center t-gap-1">
          <ConditionalToolTip
            condition={quantity <= 1 && "Minimum quantity is 1"}
          >
            <span>
              <Button
                size="small"
                customType="ghost_icon"
                onClick={() => {
                  const newQuantity = Math.max(1, quantity - 1);
                  setFieldValue(`rows.${index}.quantity`, newQuantity);
                }}
                type="button"
                disabled={quantity <= 1 || !shouldShowQuantityUpdator}
              >
                <span className="t-text-text-30">
                  <MinusCircle color="currentColor" />
                </span>
              </Button>
            </span>
          </ConditionalToolTip>
          <div className="t-w-10">
            <input
              disabled={!shouldShowQuantityUpdator}
              type="number"
              value={quantity}
              onChange={(e) => {
                const value = Math.max(1, Number(e.target.value));
                setFieldValue(`rows.${index}.quantity`, value);
              }}
              className="t-w-full t-text-center t-border t-border-solid t-border-neutral-10 t-rounded t-px-2 t-py-1 t-text-body-sm"
              min="1"
            />
          </div>
          <Button
            size="small"
            customType="ghost_icon"
            onClick={() => {
              setFieldValue(`rows.${index}.quantity`, quantity + 1);
            }}
            type="button"
            disabled={!shouldShowQuantityUpdator}
          >
            <span className="t-text-text-30">
              <PlusCircle />
            </span>
          </Button>
        </div>
      );
    },
  }),
  createColumn.display({
    id: "delete",
    header: "",
    size: 10,
    cell: (info) => {
      const index = info.row.index;
      const rowData = info.row.original;
      const hasMultipleRows = rows.length > 1;
      const rowHasValue = hasRowValue(rowData);

      const isDeleteEnabled =
        hasMultipleRows || (rows.length === 1 && rowHasValue);

      const handleDelete = () => {
        if (rows.length === 1) {
          replaceRow(index, createInitialRow());
        } else {
          removeRow(index);
        }
      };

      if (!isDeleteEnabled) {
        return null;
      }

      return (
        <Button
          size="xsmall"
          onClick={handleDelete}
          type="button"
          disabled={!isDeleteEnabled}
        >
          <span className="t-text-text-30">
            <TrashSimple />
          </span>
        </Button>
      );
    },
  }),
];
